import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const CGV = () => {
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Animation sobre d'entrée de la page
    const tl = gsap.timeline();
    
    tl.fromTo(headerRef.current.querySelector('h1'),
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" }
    )
    .fromTo(headerRef.current.querySelector('.lead'),
      { opacity: 0 },
      { opacity: 1, duration: 0.6, ease: "power2.out" },
      "-=0.4"
    );

    // Animation sobre du contenu
    gsap.fromTo(contentRef.current.querySelectorAll('h2'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: contentRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="cgv-page">
      {/* Header */}
      <section ref={headerRef} className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>Conditions Générales de Vente</h1>
            <p className="lead">
              Modalités et conditions de mes services d'éducation et de rééducation canine
            </p>
          </div>
        </div>
      </section>

      {/* Contenu des CGV */}
      <section className="section">
        <div className="container">
          <div className="cgv-content" ref={contentRef}>
            <div className="cgv-highlight">
              <p style={{ fontWeight: 'bold', marginBottom: 'var(--spacing-sm)' }}>
                Les présentes Conditions Générales de Vente (CGV) ont pour objet de définir les droits et obligations contractuels entre Atout Canin, entreprise d'éducation et de rééducation canine, et toute personne physique ou morale souscrivant à l'un des services proposés.
              </p>
              <p>
                <strong>La commande d'un service implique l'acceptation sans réserve des présentes CGV.</strong> En réglant une prestation, le client déclare avoir pris connaissance des présentes et en accepter l'intégralité sans réserve.
              </p>
              <p>
                Atout Canin se réserve le droit de modifier à tout moment les présentes CGV. Les conditions applicables sont celles en vigueur à la date de la commande.
              </p>
            </div>

            <h2>1. Prestations proposées</h2>
            <p>Atout Canin propose les services suivants :</p>
            <ul>
              <li>Bilans comportementaux (à domicile ou en extérieur)</li>
              <li>Séances d'éducation individuelles</li>
              <li>Forfaits de 3 ou 5 séances</li>
              <li>Rééducation comportementale</li>
              <li>Balades collectives encadrées</li>
              <li>Ateliers de détection d'odeurs</li>
              <li>Séances de "tricks funs" (jeux éducatifs et ludiques)</li>
              <li>Services de balades</li>
              <li>Garde à domicile</li>
              <li>Garde de jour (à venir)</li>
            </ul>

            <h2>2. Modalités de paiement</h2>
            <p>
              Le paiement des prestations est exigible en totalité lors du premier rendez-vous, ou au moment de la réservation pour les prestations collectives. Les règlements sont acceptés en espèces ou par chèque (banque domiciliée en France, en euros). Sur demande expresse, l'encaissement des chèques peut être échelonné.
            </p>

            <h2>3. Bilans comportementaux</h2>
            <p>
              Le bilan comportemental est requis en amont de tout accompagnement comportemental. Il permet de poser un diagnostic, d'évaluer les besoins et de proposer un plan d'action personnalisé. Il est facturé séparément et ne peut être intégré à un forfait.
            </p>

            <h2>4. Validité des forfaits</h2>
            <p>
              Les forfaits de 3 ou 5 séances sont valables quatre (4) mois à compter de la date du premier rendez-vous. Au-delà de ce délai, les séances non utilisées seront perdues. Aucun remboursement ne sera effectué sauf en cas de force majeure (justificatif obligatoire).
            </p>

            <h2>5. Annulation ou report de séance</h2>
            <p>
              Toute annulation de séance individuelle doit être signalée au moins 24 heures avant l'horaire prévu. À défaut, la séance est considérée comme effectuée et sera facturée ou déduite du forfait en cours. Aucune exception ne sera acceptée.
            </p>
            <p>
              Atout Canin se réserve le droit d'annuler ou de reporter un rendez-vous en cas de force majeure ou de conditions météorologiques mettant en danger les animaux. Un nouveau rendez-vous sera proposé dans les plus brefs délais.
            </p>

            <h2>6. Balades collectives et ateliers</h2>
            <p>
              Les activités collectives (balades, détection d'odeurs, tricks funs) sont soumises à inscription préalable et paiement intégral. En cas d'annulation par le client moins de 48h avant l'événement, aucun remboursement ni report ne sera accordé (sauf justificatif médical ou cas de force majeure).
            </p>
            <p>
              Atout Canin se réserve le droit de refuser la participation d'un chien présentant un comportement inadapté (agressivité, phobies, etc.). Un entretien préalable peut être exigé.
            </p>

            <h2>7. Conditions de participation</h2>
            <p>Tout chien participant aux activités de Atout Canin doit :</p>
            <ul>
              <li>Être identifié (puce ou tatouage)</li>
              <li>Être à jour de ses vaccinations</li>
              <li>Être traité contre les parasites</li>
              <li>Être âgé d'au moins 3 mois</li>
            </ul>
            <p>
              En cas de comportement dangereux ou de maladie contagieuse, Atout Canin pourra exclure immédiatement l'animal, sans possibilité de remboursement.
            </p>

            <h2>8. Droit de rétractation</h2>
            <p>
              Conformément à l'article L121-21 du Code de la consommation, le client dispose d'un délai de rétractation de 8 jours à compter de la signature du contrat. Toute rétractation doit être notifiée par courrier recommandé à l'adresse de l'entreprise.
            </p>
            <p>
              Passé ce délai, aucun remboursement ne sera effectué. Une exception peut être envisagée en cas de décès du chien ou de maladie grave empêchant la poursuite des séances (certificat vétérinaire requis).
            </p>

            <h2>9. Responsabilité</h2>
            <p>
              Atout Canin est tenue à une obligation de moyens et non de résultat. Le chien reste sous la responsabilité permanente de son propriétaire, y compris pendant les séances. Atout Canin ne pourra être tenue responsable de dommages causés ou subis par le chien.
            </p>

            <h2>10. Données personnelles</h2>
            <p>
              Les données collectées sont strictement confidentielles. Conformément à la loi Informatique et Libertés n°78-17 du 6 janvier 1978, le client dispose d'un droit d'accès, de modification, de rectification et de suppression des données le concernant.
            </p>
            <p>
              Atout Canin s'engage à ne pas céder les données personnelles à des tiers.
            </p>

            <h2>11. Réclamations</h2>
            <p>
              Toute réclamation concernant une prestation doit être adressée par écrit dans les plus brefs délais à Atout Canin, qui s'efforcera d'y répondre avec diligence.
            </p>

            <h2>12. Médiation à la consommation</h2>
            <p>
              Conformément à l'article L.612-1 du Code de la consommation, tout consommateur a le droit de recourir gratuitement à un médiateur de la consommation en vue de la résolution amiable d'un litige.
            </p>

            <h2>13. Tribunal compétent</h2>
            <p>
              En cas de litige non résolu à l'amiable, le tribunal compétent sera celui du lieu du domicile du défendeur, sauf dispositions légales contraires.
            </p>

            <h2>14. Utilisation d’images</h2>
            <p>
              Le client autorise Atout Canin à prendre des photos ou vidéos de son chien dans le cadre des activités, à des fins de communication (site internet, réseaux sociaux, etc.). Le client peut à tout moment s'opposer à cette utilisation par simple demande écrite.
            </p>

            <h2>15. Tarifs et TVA</h2>
            <p>
              Les prix sont indiqués en euros, nets de TVA (TVA non applicable selon l’article 293B du Code général des impôts). Atout Canin se réserve le droit de modifier ses tarifs à tout moment. Les prestations seront facturées sur la base du tarif en vigueur au moment de la commande.
            </p>

            <h2>16. Assurance professionnelle</h2>
            <p>
              Atout Canin est couverte par une assurance responsabilité civile professionnelle pour l’exercice de ses activités d’éducation et de rééducation canine.
            </p>

            <div className="text-center mt-xl">
              <Link to="/contact" className="btn btn-primary btn-large">
                Une question sur mes CGV ?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Informations pratiques */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2>Informations Pratiques</h2>
            <p className="lead">
              Résumé des points essentiels à retenir
            </p>
          </div>
          
          <div className="grid grid-3">
            <div className="card">
                              <h4>Paiement</h4>
              <ul>
                <li>Lors du premier rendez-vous</li>
                <li>Espèces ou chèque</li>
                <li>Échelonnement possible</li>
              </ul>
            </div>
            
            <div className="card">
              <h4>Annulation</h4>
              <ul>
                <li>24h pour séances individuelles</li>
                <li>48h pour activités collectives</li>
                <li>Facturation si délai non respecté</li>
              </ul>
            </div>
            
            <div className="card">
                              <h4>Validité</h4>
              <ul>
                <li>Forfaits valables 4 mois</li>
                <li>À partir du premier rendez-vous</li>
                <li>Pas de remboursement sauf force majeure</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Atout Canin - Éducation et Rééducation Canine. Tous droits réservés.</p>
          <p>
            <Link to="/">Retour à l'accueil</Link>
            <Link to="/contact">Nous contacter</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CGV; 